import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/api/resources/auth/hooks";
import { useCurrentDeployment, useFilteredDeployments } from "@/api/resources/deployments/hooks";
import { useCurrentWorkspace } from "@/api/resources/workspaces/hooks";
import { useGlobalFilters } from "@/hooks/useGlobalFilters";
import { DeploymentRole, WorkspaceRole } from "@/supabase.types";

export interface WorkspaceScope {
  workspace_id?: string;
}

export interface DeploymentScope {
  deployment_id?: string;
}

export enum Permission {
  Edit = "edit",
  View = "view",
}

export enum FeaturePermission {
  Cases = "cases",
  CasesReport = "cases-report",
  Maintenances = "maintenances",
  PartsDevice = "parts-device",
}

interface PermissionLookup {
  [Permission.Edit]?: boolean;
  [Permission.View]?: boolean;
}
type EntityPermissionLookup = Record<string, PermissionLookup>; // key is workspace_id OR deployment_id
type FeatureLookup = Record<FeaturePermission, PermissionLookup>; // key is typeof FeaturePermission (cases, maintenances, ...)
type FeaturePermissionLookup = Record<string, FeatureLookup>; // key is workspace_id OR deployment_id

export const useRoles = () => {
  const { t } = useTranslation();
  const { permissions: userPermissions } = useAuth();
  const { options } = useGlobalFilters();
  const { data: currentWorkspace } = useCurrentWorkspace();
  const { data: currentDeployment } = useCurrentDeployment();
  const { data: filteredDeployments } = useFilteredDeployments();

  const availableWorkspaceRoles = useMemo(
    () => [
      { value: WorkspaceRole.admin, label: t("admin"), description: t(`role-explanation.workspace.admin`) },
      { value: WorkspaceRole.viewer, label: t("viewer"), description: t(`role-explanation.workspace.viewer`) },
    ],
    [t],
  );
  const availableDeploymentRoles = useMemo(
    () => [
      { value: DeploymentRole.admin, label: t("admin"), description: t(`role-explanation.deployment.admin`) },
      {
        value: DeploymentRole.technician,
        label: t("technician"),
        description: t(`role-explanation.deployment.technician`),
      },
      {
        value: DeploymentRole.end_customer,
        label: t("end_customer"),
        description: t(`role-explanation.deployment.end_customer`),
      },
      { value: DeploymentRole.viewer, label: t("viewer"), description: t(`role-explanation.deployment.viewer`) },
    ],
    [t],
  );

  const globalPermissions = useMemo(() => {
    // typescript hack to allow for key in permission
    const permissions: PermissionLookup = {
      [Permission.Edit]: false,
      [Permission.View]: false,
    };
    if (userPermissions?.globalAdmin) {
      permissions[Permission.Edit] = false; // ! GlobalAdmins can view all things, but can't write anything
      permissions[Permission.View] = true;
    }
    return permissions;
  }, [userPermissions]);

  const workspacePermissions = useMemo(() => {
    let permissions: EntityPermissionLookup = {};
    const featurePermissions: FeaturePermissionLookup = {};

    if (!userPermissions?.workspace) {
      return {
        permissions,
        featurePermissions,
      };
    }

    for (const { workspace_id, role } of userPermissions.workspace) {
      if (role === WorkspaceRole.admin) {
        permissions = {
          ...permissions,
          [workspace_id]: {
            ...permissions[workspace_id],
            [Permission.Edit]: true,
          },
        };
      }
      if (role === WorkspaceRole.viewer) {
        permissions = {
          ...permissions,
          [workspace_id]: {
            ...permissions[workspace_id],
            [Permission.View]: true,
          },
        };
      }
    }

    return { permissions, featurePermissions };
  }, [userPermissions]);

  const deploymentPermissions = useMemo(() => {
    let permissions: EntityPermissionLookup = {};
    let featurePermissions: FeaturePermissionLookup = {};

    if (!userPermissions?.deployment) {
      return {
        permissions,
        featurePermissions,
      };
    }

    for (const { deployment_id, role } of userPermissions.deployment) {
      if (role === DeploymentRole.admin) {
        permissions = {
          ...permissions,
          [deployment_id]: {
            ...permissions[deployment_id],
            [Permission.Edit]: true,
          },
        };
      }
      if (role === DeploymentRole.end_customer) {
        permissions = {
          ...permissions,
          [deployment_id]: {
            ...permissions[deployment_id],
            [Permission.View]: true,
          },
        };
        featurePermissions = {
          ...featurePermissions,
          [deployment_id]: {
            ...featurePermissions[deployment_id],
            [FeaturePermission.CasesReport]: {
              [Permission.Edit]: true,
            },
          },
        };
      }
      if (role === DeploymentRole.technician) {
        permissions = {
          ...permissions,
          [deployment_id]: {
            ...permissions[deployment_id],
            [Permission.View]: true,
          },
        };
        featurePermissions = {
          ...featurePermissions,
          [deployment_id]: {
            ...featurePermissions[deployment_id],
            [FeaturePermission.CasesReport]: {
              [Permission.Edit]: true,
            },
            [FeaturePermission.Cases]: {
              [Permission.Edit]: true,
            },
            [FeaturePermission.Maintenances]: {
              [Permission.Edit]: true,
            },
            [FeaturePermission.PartsDevice]: {
              [Permission.Edit]: true,
            },
          },
        };
      }
      if (role === DeploymentRole.viewer) {
        permissions = {
          ...permissions,
          [deployment_id]: {
            ...permissions[deployment_id],
            [Permission.View]: true,
          },
        };
      }
    }

    return { permissions, featurePermissions };
  }, [userPermissions]);

  const hasGlobalPermission = useCallback((perm: Permission) => !!globalPermissions[perm], [globalPermissions]);
  const hasWorkspacePermission = useCallback(
    (perm: Permission, { workspace_id }: WorkspaceScope, feature?: FeaturePermission) => {
      let permitted = false;
      if (!workspace_id) return permitted;
      const { permissions, featurePermissions } = workspacePermissions;

      if (workspace_id in permissions) {
        permitted = !!permissions[workspace_id][perm];
      }

      if (feature && workspace_id in featurePermissions && feature in featurePermissions[workspace_id]) {
        permitted = !!featurePermissions[workspace_id][feature];
      }

      return permitted;
    },
    [workspacePermissions],
  );
  const hasDeploymentPermission = useCallback(
    (perm: Permission, { deployment_id }: DeploymentScope, feature?: FeaturePermission) => {
      let permitted = false;
      if (!deployment_id) return permitted;
      const { permissions, featurePermissions } = deploymentPermissions;

      if (deployment_id in permissions) {
        permitted = !!permissions[deployment_id][perm];
      }

      if (feature && deployment_id in featurePermissions && feature in featurePermissions[deployment_id]) {
        permitted = !!featurePermissions[deployment_id][feature];
      }

      return permitted;
    },
    [deploymentPermissions],
  );

  const hasPermission = useCallback(
    (permission: Permission, context?: WorkspaceScope | DeploymentScope, feature?: FeaturePermission): boolean => {
      if (hasGlobalPermission(permission)) return true;
      if (context) {
        if ("deployment_id" in context) {
          return hasDeploymentPermission(permission, context, feature);
        }
        if ("workspace_id" in context) {
          return hasWorkspacePermission(permission, context, feature);
        }
      }
      return false;
    },
    [hasDeploymentPermission, hasGlobalPermission, hasWorkspacePermission],
  );

  const hasPermissions = useCallback(
    (permissions: Permission[], context?: WorkspaceScope | DeploymentScope, feature?: FeaturePermission): boolean => {
      for (const perm of permissions) {
        if (!hasPermission(perm, context, feature)) return false;
      }
      return true;
    },
    [hasPermission],
  );

  const canEditDeployment = useCallback(
    (context?: DeploymentScope, feature?: FeaturePermission): boolean => {
      if (!context?.deployment_id) {
        return false;
      }

      return hasPermission(Permission.Edit, { deployment_id: context.deployment_id }, feature);
    },
    [hasPermission],
  );
  const canEditWorkspace = useCallback(
    (context?: WorkspaceScope, feature?: FeaturePermission): boolean => {
      if (!context?.workspace_id) {
        return false;
      }

      return hasPermission(Permission.Edit, { workspace_id: context.workspace_id }, feature);
    },
    [hasPermission],
  );
  const canEdit = useCallback(
    (context?: WorkspaceScope | DeploymentScope, feature?: FeaturePermission): boolean => {
      if (!context) {
        return false;
      }

      return hasPermission(Permission.Edit, context, feature);
    },
    [hasPermission],
  );

  const canViewDeployment = (context?: DeploymentScope, feature?: FeaturePermission): boolean => {
    if (!context?.deployment_id) {
      return false;
    }

    return hasPermission(Permission.View, { deployment_id: context.deployment_id }, feature);
  };
  const canViewWorkspace = (context?: WorkspaceScope, feature?: FeaturePermission): boolean => {
    if (!context?.workspace_id) {
      return false;
    }

    return hasPermission(Permission.View, { workspace_id: context.workspace_id }, feature);
  };
  const canView = (context?: WorkspaceScope | DeploymentScope, feature?: FeaturePermission): boolean => {
    return hasPermission(Permission.View, context, feature);
  };

  const canEditCurrentWorkspace = useMemo(() => {
    if (currentWorkspace) return canEditWorkspace({ workspace_id: currentWorkspace.id });
    return false;
  }, [canEditWorkspace, currentWorkspace]);

  const canEditCurrentDeployment = useMemo(() => {
    if (currentDeployment) return canEditDeployment({ deployment_id: currentDeployment.id });
    return false;
  }, [canEditDeployment, currentDeployment]);

  const canEditCurrentWorkspaceOrFilteredDeployments = useMemo(() => {
    let _canEditCurrentWorkspaceOrFilteredDeployments = false;
    if (currentWorkspace) {
      _canEditCurrentWorkspaceOrFilteredDeployments = canEditWorkspace({
        workspace_id: currentWorkspace.id,
      });
    }

    if (options.deployments.length > 0) {
      options.deployments.forEach((d) => {
        _canEditCurrentWorkspaceOrFilteredDeployments =
          _canEditCurrentWorkspaceOrFilteredDeployments || canEditDeployment({ deployment_id: d });
      });
    } else if (filteredDeployments && filteredDeployments.length > 0) {
      filteredDeployments.forEach((d) => {
        _canEditCurrentWorkspaceOrFilteredDeployments =
          _canEditCurrentWorkspaceOrFilteredDeployments || canEditDeployment({ deployment_id: d.id });
      });
    }

    return _canEditCurrentWorkspaceOrFilteredDeployments;
  }, [canEditDeployment, canEditWorkspace, currentWorkspace, filteredDeployments, options.deployments]);

  const canEditFilteredDeployments = useMemo(() => {
    let _canEditFilteredDeployments = false;

    if (options.deployments.length > 0) {
      options.deployments.forEach((d) => {
        _canEditFilteredDeployments = _canEditFilteredDeployments || canEditDeployment({ deployment_id: d });
      });
    } else if (filteredDeployments && filteredDeployments.length > 0) {
      filteredDeployments.forEach((d) => {
        _canEditFilteredDeployments = _canEditFilteredDeployments || canEditDeployment({ deployment_id: d.id });
      });
    }

    return _canEditFilteredDeployments;
  }, [canEditDeployment, filteredDeployments, options.deployments]);

  const canEditFeatureInCurrentWorkspaceOrFilteredDeployments = (feature: FeaturePermission) => {
    let _canEditCurrentWorkspaceOrFilteredDeployments = false;
    if (currentWorkspace) {
      _canEditCurrentWorkspaceOrFilteredDeployments = canEditWorkspace(
        {
          workspace_id: currentWorkspace.id,
        },
        feature,
      );
    }
    if (options.deployments.length > 0) {
      options.deployments.forEach((d) => {
        _canEditCurrentWorkspaceOrFilteredDeployments =
          _canEditCurrentWorkspaceOrFilteredDeployments || canEditDeployment({ deployment_id: d }, feature);
      });
    } else if (filteredDeployments && filteredDeployments.length > 0) {
      filteredDeployments.forEach((d) => {
        _canEditCurrentWorkspaceOrFilteredDeployments =
          _canEditCurrentWorkspaceOrFilteredDeployments || canEditDeployment({ deployment_id: d.id }, feature);
      });
    }
    return _canEditCurrentWorkspaceOrFilteredDeployments;
  };

  const canEditFeatureInFilteredDeployments = (feature: FeaturePermission) => {
    let _canEditFeatureInFilteredDeployments = false;
    if (options.deployments.length > 0) {
      options.deployments.forEach((d) => {
        _canEditFeatureInFilteredDeployments =
          _canEditFeatureInFilteredDeployments || canEditDeployment({ deployment_id: d }, feature);
      });
    } else if (filteredDeployments && filteredDeployments.length > 0) {
      filteredDeployments.forEach((d) => {
        _canEditFeatureInFilteredDeployments =
          _canEditFeatureInFilteredDeployments || canEditDeployment({ deployment_id: d.id }, feature);
      });
    }
    return _canEditFeatureInFilteredDeployments;
  };

  const isGlobalAdmin = useMemo(() => {
    return userPermissions?.globalAdmin;
  }, [userPermissions]);

  return {
    isGlobalAdmin,
    availableWorkspaceRoles,
    availableDeploymentRoles,
    hasPermission,
    hasPermissions,
    canEdit,
    canEditDeployment,
    canEditWorkspace,
    canView,
    canViewDeployment,
    canViewWorkspace,
    canEditCurrentWorkspace,
    canEditCurrentDeployment,
    canEditCurrentWorkspaceOrFilteredDeployments,
    canEditFilteredDeployments,
    canEditFeatureInCurrentWorkspaceOrFilteredDeployments,
    canEditFeatureInFilteredDeployments,
  };
};
